import { useAnalytics } from '@inflearn/analytics';
import { SkeletonHeader } from '@inflearn/app-shell';
import { Flex } from '@inflearn/ds-react';
import { useLang } from '@inflearn/i18n-tools';
import { useQueryClient } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { type ReactNode, Suspense } from 'react';

import { useReferUrlNavigation } from '../../hooks/useReferUrlNavigation';
import { useSession } from '../../hooks/useSession';
import { getCurrentLanguage } from '../../utils/i18n/getCurrentLanguage';
import { DefaultFallback } from './DefaultFallback';
import { UnAuthorizedHeader } from './UnAuthorizedHeader';

const AppShellProvider = dynamic(() => import('appShell/AppShellProvider'), {
  ssr: false,
  loading: () => <SkeletonHeader lang={getCurrentLanguage()} />,
});
const AppShell = dynamic(() => import('appShell/AppShell'), {
  ssr: false,
  loading: () => <SkeletonHeader lang={getCurrentLanguage()} />,
});
const Header = dynamic(() => import('appShell/Header'), {
  ssr: false,
  loading: () => <SkeletonHeader lang={getCurrentLanguage()} />,
});
const Footer = dynamic(() => import('appShell/Footer'), {
  ssr: false,
  loading: () => <SkeletonHeader lang={getCurrentLanguage()} />,
});
const LoginModal = dynamic(() => import('appShell/LoginModal'), {
  ssr: false,
  loading: () => <SkeletonHeader lang={getCurrentLanguage()} />,
});

type Props = {
  children: ReactNode;
  requiredLogin: boolean;
  showChanneltalk: boolean;
  showLanguageSectionButton?: boolean;
  useUnauthorizedHeader?: boolean;
  hasFNB?: boolean;
};

export default function AppShellLayoutForCSR({
  children,
  requiredLogin,
  showChanneltalk,
  showLanguageSectionButton = true,
  useUnauthorizedHeader = false,
  hasFNB = true,
}: Props) {
  const { analytics } = useAnalytics();
  const queryClient = useQueryClient();
  const { isKo } = useLang();

  return (
    <AppShellProvider analytics={analytics} queryClient={queryClient}>
      <AppShell
        header={
          useUnauthorizedHeader ? (
            <UnAuthorizedHeader />
          ) : (
            <Header showLanguageSectionButton={showLanguageSectionButton} />
          )
        }
        footer={<Footer hasFNB={hasFNB} />}
        showChanneltalk={isKo ? showChanneltalk : false}
      >
        <Suspense fallback={<DefaultFallback mih={800} />}>
          <RequiredLoginWrapper requiredLogin={requiredLogin}>{children}</RequiredLoginWrapper>
        </Suspense>
      </AppShell>
    </AppShellProvider>
  );
}

/**
 *
 * @description Suspense 적용을 위해 컴포넌트 분리용 Wrapper
 */
function RequiredLoginWrapper({
  children,
  requiredLogin,
}: {
  children: ReactNode;
  requiredLogin: boolean;
}) {
  const { isLogin } = useSession({
    suspense: true,
    useErrorBoundary: false,
    enabled: requiredLogin,
  });
  const { navigateMainUrl } = useReferUrlNavigation();

  if (requiredLogin && !isLogin) {
    return (
      <Flex component="main" mih={800}>
        <LoginModal opened onClose={navigateMainUrl} />
      </Flex>
    );
  }

  return <>{children}</>;
}
